<template>
	<v-dialog
		v-model="dialog"
		max-width="fit-content"
		scrollable>

		<div v-shortkey="['esc']" @shortkey="dialog = false"></div>

		<template v-slot:activator="{ on }">
			<v-btn
				flat icon absolute right large
				class="ma-0 activator-btn"
				color="primary"
				v-on="on"
				title="Documentation">
				<v-icon large>info</v-icon>
			</v-btn>
		</template>

		<v-card class="background">
			<v-card-title class="py-1 background darken-1">
				<v-icon color="primary">info</v-icon>
				<span class="ml-2 headline">
					Documentation de l'explorateur de fichiers
				</span>
				<v-spacer></v-spacer>
				<v-btn icon @click="dialog = false">
					<v-icon large>close</v-icon>
				</v-btn>
			</v-card-title>
			<v-card-text>
				<v-container class="pa-0" grid-list-xl>
					<v-layout row wrap>

						<v-flex xs8>
							<div class="headline primary--text mb-2">
								Champs de recherche
							</div>

							<div class="subheading sub-titles">
								Points de montage
							</div>
							<div class="content-section">
								Permet d'auto-remplir le champ <strong>chemin</strong> avec
								les chemins racine des serveurs de stockage listés.
								<br>
								Les boutons
								<v-icon small color="cyan accent-4">star</v-icon>
								permettent d'ajouter la racine d'un serveur aux favoris.
								<br>
								Si un stockage est manquant dans la liste,
								il est toujours possible de tenter de saisir son chemin
								manuellement dans le champ <strong>chemin</strong>.
								<br>
								Au besoin vous pouvez faire une demande à
								<a :href="mailto" target="_blank">l'équipe de developpement</a>
								pour que votre serveur soit ajouté à la liste des points de
								montage.
							</div>

							<div class="subheading sub-titles">
								Chemin
							</div>
							<div class="content-section">
								Emplacement du chemin en cours, plusieurs fonctionnalités :
								<table class="icon-list">
									<tr>
										<th>
											<v-icon>mode_edit</v-icon>
										</th>
										<td>
											Saisie manuelle du chemin.
										</td>
									</tr>
									<tr>
										<th>
										<v-icon color="cyan accent-4">star</v-icon>
										</th>
										<td>
											Selection d'un chemin dans la liste de vos favoris.
										</td>
									</tr>
									<tr>
										<th>
											<v-icon>history</v-icon>
										</th>
										<td>
											Selection d'un chemin dans la liste des 10 derniers
											chemins parcourus.
										</td>
									</tr>
									<tr>
										<th>
											<v-icon>chevron_left</v-icon>
										</th>
										<td>
											Le bouton à gauche du champ permet de remonter d'un
											niveau dans l'arborescence en cours.
										</td>
									</tr>
								</table>
							</div>

							<div class="subheading sub-titles">
								Filtrer
							</div>
							<div class="content-section">
								Permet de filtrer la liste des dossiers et fichiers visibles.
								<br>
								Il filtre unqiuement ce qui est visible dans le tableau,
								il ne recherchera pas dans les sous dossiers non dépliés.
							</div>

							<div class="headline primary--text mt-3">
								Fonctionnalités de l'explorateur
							</div>

							<div class="subheading sub-titles">
								Tri
							</div>
							<div class="content-section">
								En cliquant sur les entêtes du tableau,
								il est possible de trier le tableau par :
								<ul>
									<li>Nom</li>
									<li>Poids</li>
									<li>Date de modification</li>
								</ul>
							</div>

							<div class="subheading sub-titles">
								Navigation
							</div>
							<div class="content-section">
								Deux façons de naviguer sont possibles :
								<ul>
									<li>
										Exploration de l'arborescence d'un dossier
										<i>(le champ <strong>chemin</strong> reste inchangé)</i> :
										<ul>
											<li>Au clic sur l'icon de dossier</li>
											<li>Au double-clic sur le nom du dossier</li>
										</ul>
									</li>
									<li>
										Navigation vers le contenu d'un dossier
										<i>(le champ <strong>chemin</strong> est mis à jour)</i> :
										<ul>
											<li>
												Au control + clic sur le nom du dossier,
												(<v-icon small>keyboard_command_key</v-icon> + click sous MAC)
											</li>
											<li>
												Au clic-droit
												<strong>Naviguez vers //xxx/xx/xxx</strong>
											</li>
										</ul>
									</li>
								</ul>
								De plus, à chaque navigation,
								l'URL de votre navigateur est mise à jour.
								<br>
								L'URL peut donc être partagée, et copiable dans un email.
							</div>

							<div class="subheading sub-titles">
								Actions possible
							</div>
							<div class="content-section">
								<table class="icon-list">
									<tr>
										<th>
											<v-icon color="pink lighten-4">
												check_box
											</v-icon>
										</th>
										<td class="pink--text text--lighten-4">
											Sélectionner / Désélectionnner un élément.
										</td>
									</tr>
									<tr>
										<th>
											<v-icon color="green accent-3">
												content_copy
											</v-icon>
										</th>
										<td class="green--text text--accent-3">
											Copier dans le presse papier le chemin de l'élément.
											<i>
												Si vous etes sous Windows le chemin sera de type UNC,
												sinon du type Maestro.
											</i>
										</td>
									</tr>
									<tr>
										<th>
											<v-icon color="white lighten-1">
												folder
											</v-icon>
										</th>
										<td class="white--text text--lighten-1">
											Ouvrir l'élément dans votre navigateur Windows
											via le <strong>FileOpener</strong>.
										</td>
									</tr>
									<tr>
										<th>
											<v-icon color="light-blue accent-2">
												video_settings
											</v-icon>
										</th>
										<td class="light-blue--text text--accent-2">
											Lancer un probe du fichier
											<i>(seulement pour les fichiers)</i>.
										</td>
									</tr>
									<tr>
										<th>
											<v-icon color="orange accent-2">
												find_in_page
											</v-icon>
										</th>
										<td class="orange--text text--accent-2">
											Visualiser le contenu d'un fichier léger
											<i>
												Peut être utile pour de petit documents
												(txt, json, avs etc...).
											</i>
										</td>
									</tr>
									<tr>
										<th>
											<v-icon color="cyan accent-4">
												star
											</v-icon>
										</th>
										<td class="cyan--text text--accent-4">
											Ajouter un élément à vos favoris,
											permet de rappeler un chemin utilisé souvent
											via le selecteur de <strong>chemin</strong>.
										</td>
									</tr>
									<tr>
										<th>
											<v-icon color="primary">
												play_arrow
											</v-icon>
										</th>
										<td class="primary--text">
											Lancer une demande travail Maestro.
										</td>
									</tr>
									<tr>
										<th>
											<v-icon color="deep-purple lighten-4">
												arrow_forward
											</v-icon>
										</th>
										<td class="deep-purple--text text--lighten-4">
											Naviguer vers le chemin du dossier.
										</td>
									</tr>
									<tr>
										<th>
											<v-icon color="orange accent-2">
												description
											</v-icon>
										</th>
										<td class="orange--text text--accent-2">
											Télécharger un csv listant le contenu du chemin en cours.
										</td>
									</tr>
								</table>
							</div>
						</v-flex>

						<v-flex xs4>
							<div class="headline primary--text mb-3">Raccourcis clavier</div>
							<table>
								<tr>
									<th class="keyboard-sc elevation-8">
										<div v-if="$OS === 'MacOS'">
											<v-icon small>keyboard_command_key</v-icon> + <v-icon small>arrow_back</v-icon>
										</div>
										<div v-else>
											Ctrl + <v-icon small>arrow_back</v-icon>
										</div>
									</th>
									<td>
										Remonter d'un niveau dans l'arborescence en cours.
									</td>
								</tr>
								<tr>
									<th class="keyboard-sc elevation-8">
										<div v-if="$OS === 'MacOS'">
											<v-icon small>keyboard_command_key</v-icon> + <v-icon small>arrow_upward</v-icon>
										</div>
										<div v-else>
											Ctrl + <v-icon small>arrow_upward</v-icon>
										</div>
									</th>
									<td>
										Remonter à la racine de l'arborescence en cours.
									</td>
								</tr>
								<tr>
									<th class="keyboard-sc elevation-8">
										<div v-if="$OS === 'MacOS'">
											<v-icon small>keyboard_command_key</v-icon> + Clic
										</div>
										<div v-else>Ctrl + Clic</div>
									</th>
									<td>
										Sur un dossier, navigue vers le dossier cliqué.
									</td>
								</tr>
								<tr>
									<th class="keyboard-sc elevation-8">
										<div v-if="$OS === 'MacOS'">
											<v-icon small>keyboard_option_key</v-icon> + F
										</div>
										<div v-else>
											Alt + F
										</div>
									</th>
									<td>
										Mettre le focus sur le champ <strong>Filtrer</strong>.
									</td>
								</tr>
							</table>

							<div class="headline primary--text mb-3 mt-3">
								Uploader de fichiers
							</div>
							<div class="subheading sub-titles">
								Pour uploader un ou plusieurs fichiers, voici la marche à suivre :
							</div>
							<div class="content-section">
								<ol>
									<li>
										Naviguer vers le dossier ou vous voulez déposer des fichiers.
										<br>
										(Le chemin dans la barre du haut doit bien etre le chemin du dossier.)
									</li>
									<li>
										Glissez-déposez vos fichiers sur l'explorateur de gravity
									</li>
									<li>
										Une boite de dialogue s'ouvrira pour lancer les uploads.
										<br>
										(Les uploads seront possible si les fichiers respectent certaines conditions.)
									</li>
								</ol>
							</div>
							<div class="subheading sub-titles">
								Conditions à l'upload d'un fichier :
							</div>
							<div class="content-section">
								<ul>
									<li>
										Poids du fichier &#60; {{ Math.round((uploader_max_file_size / 1024)) }} Mo
									</li>
									<li>
										Le chemin de destination doit se trouver dans les
										arborescences suivantes :
										<div
											v-for="(item, i) in uploader_valid_destination_paths"
											:key="'path_' + i">
											<v-icon small>folder</v-icon>
											{{ item.path }}
										</div>
									</li>
									<li>
										Les extensions autorisées sont :
										<v-chip label small color="background darken-1"
											v-for="(ext, i) in uploader_allowed_extensions"
											:key="ext + i">
											{{ ext }}
										</v-chip>
									</li>
								</ul>
							</div>

							<div class="headline primary--text mb-3 mt-3">
								Création de dossier
							</div>
							<div class="subheading sub-titles">
								Pour créer un dossier, voici la marche à suivre :
							</div>
							<div class="content-section">
								<ol>
									<li>
										Naviguer vers le dossier ou vous voulez déposer des fichiers.
									</li>
									<li>
										Au clic droit,
										si le chemin en cours permet la création de dossier,
										un bouton "Créer un nouveau dossier"
										sera disponible dans le menu contextuel.
									</li>
									<li>
										Une boite de dialogue s'ouvrira pour nommer le dossier
										et lancer la création du dossier
									</li>
								</ol>
							</div>
							<div class="subheading sub-titles">
								La création de dossier n'est possible que dans les
								arborescences suivantes :
							</div>
							<div class="content-section">
								<div
									v-for="(item, i) in uploader_valid_destination_paths"
									:key="'path_' + i">
									<v-icon small>folder</v-icon>
									{{ item.path }}
								</div>
							</div>
						</v-flex>
					</v-layout>
				</v-container>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
import {
	ALLOWED_EXTENSIONS,
	VALID_DESTINATION_PATHS,
	MAX_FILE_SIZE
} from '@/pages/filesystem/explorer_uploader.js'

export default {
	data () {
		return {
			uploader_allowed_extensions: ALLOWED_EXTENSIONS,
			uploader_valid_destination_paths: VALID_DESTINATION_PATHS,
			uploader_max_file_size: MAX_FILE_SIZE,
			dialog: false,
			mailto: 'https://mail.google.com/mail/u/0/?fs=1&to=dev@hiventy.com&su=Gravity&tf=cm'
		}
	}
}
</script>

<style lang="stylus" scoped>
.sub-titles
	margin: 10px 0 6px 20px
	color: var(--v-primary-lighten1)

.content-section
	margin: 0 0 0 40px

table.icon-list
	th
		padding: 2px
		vertical-align: top
		.v-icon
			margin-right: 4px
			font-size: 28px

.keyboard-sc
	padding: 4px 10px
	background-color: var(--v-background-darken1)
	display: block
	border: 1px solid var(--v-background-lighten1)
	margin: 0 10px 6px 0
	min-width: 100px
	text-align: center
	.v-icon
		margin-left: 4px
		font-size: 21px
</style>



// WEBPACK FOOTER //
// src/pages/filesystem/explorer_documentation.vue